// /user/:username/rankings — "See All Rankings" for a visitor: another
// person's PUBLIC rankings, newest first, paginated by the API cursor. The
// API applies the profile's own privacy rule (private profile → isPrivate,
// never the list) and 404s for deleted or unknown accounts. Owners manage
// theirs at /me/rankings (all visibilities); this page links there for them.
// Each ranking opens its public page, which has Make Your Own.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { RankingService } from '../../services/rankingService';
import base from '../TopMovies/TopMovies.module.css';
import styles from '../MyRankings/MyRankings.module.css';

const PAGE_SIZE = 20;
const COMPOSITION_LABEL = { movie: 'Movies', tv: 'TV', mixed: 'Movies & TV' };

export default function UserRankings() {
    const { username: rawUsername } = useParams();
    const username = rawUsername?.replace(/^@/, '');
    const { user } = useAuth();
    const isOwner = Boolean(user?.username) && user.username === username;
    const [owner, setOwner] = useState(null);
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | private | not_found | error
    const [loadingMore, setLoadingMore] = useState(false);
    const [moreError, setMoreError] = useState(false);
    const loadingMoreRef = useRef(false);
    const requestRef = useRef(0);

    const loadFirst = useCallback(async () => {
        const id = ++requestRef.current;
        setStatus('loading');
        setItems([]);
        setNextCursor(null);
        setMoreError(false);
        try {
            const page = await RankingService.listForUser(username, { limit: PAGE_SIZE });
            if (id !== requestRef.current) return;
            if (page?.user) setOwner(page.user);
            if (page?.isPrivate) { setStatus('private'); return; }
            setItems(page?.items ?? []);
            setNextCursor(page?.nextCursor ?? null);
            setStatus('ready');
        } catch (err) {
            if (id === requestRef.current) setStatus(err?.statusCode === 404 ? 'not_found' : 'error');
        }
    }, [username]);

    useEffect(() => { loadFirst(); }, [loadFirst]);

    async function loadMore() {
        if (!nextCursor || loadingMoreRef.current) return;
        const id = requestRef.current;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setMoreError(false);
        try {
            const page = await RankingService.listForUser(username, { limit: PAGE_SIZE, cursor: nextCursor });
            if (id !== requestRef.current) return;
            if (page?.isPrivate) {
                // The profile became private mid-session: drop what we had.
                setItems([]);
                setNextCursor(null);
                setStatus('private');
                return;
            }
            setItems((prev) => {
                const seen = new Set(prev.map((r) => r.shareSlug));
                return [...prev, ...(page?.items ?? []).filter((r) => !seen.has(r.shareSlug))];
            });
            setNextCursor(page?.nextCursor ?? null);
        } catch {
            if (id === requestRef.current) setMoreError(true);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }

    const name = owner?.displayName || (owner?.username ? `@${owner.username}` : `@${username}`);
    const title = `${name}'s Rankings`;
    useEffect(() => { document.title = `${title} | Aluvo`; }, [title]);

    return (
        <main className={base.page}>
            <div className={base.shell}>
                <Link to={`/user/${encodeURIComponent(username)}`} className={styles.back}>← Back to profile</Link>
                <header className={styles.header}>
                    <div>
                        <span className={base.eyebrow}>Aluvo · Rankings</span>
                        <h1>{title}</h1>
                    </div>
                    {isOwner
                        ? <Link to="/me/rankings" className={base.secondary}>Manage my rankings</Link>
                        : <Link to="/rankings/new" state={{ source: 'profile_rankings' }} className={base.primary}>Make a ranking</Link>}
                </header>

                {status === 'loading' && <p className={base.muted} role="status">Loading rankings…</p>}
                {status === 'error' && (
                    <p className={base.errorText} role="alert">
                        Couldn&apos;t load these rankings.{' '}
                        <button type="button" className={base.linkButton} onClick={loadFirst}>Try again</button>
                    </p>
                )}
                {status === 'not_found' && <p className={base.muted} role="status">This profile doesn&apos;t exist or is no longer available.</p>}
                {status === 'private' && (
                    <section className={styles.empty}>
                        <h2>🔒 This profile is private</h2>
                        <p>Rankings are only visible to people this profile is shared with.</p>
                    </section>
                )}
                {status === 'ready' && items.length === 0 && (
                    <section className={styles.empty}>
                        <h2>No public rankings yet.</h2>
                        <p>When {name} shares a ranking, it will show up here.</p>
                    </section>
                )}
                {items.length > 0 && (
                    <>
                        <ul className={styles.list} aria-label={title}>
                            {items.map((ranking) => (
                                <li key={ranking.shareSlug} className={styles.row}>
                                    <Link to={`/rankings/${ranking.shareSlug}`} className={styles.rowMain}>
                                        <span className={styles.posters} aria-hidden="true">
                                            {(ranking.previewPosters || []).slice(0, 5).map((url) => <img key={url} src={url} alt="" loading="lazy" />)}
                                            {!ranking.previewPosters?.length && <span className={styles.noPoster}>🏆</span>}
                                        </span>
                                        <span className={styles.rowText}>
                                            <strong>{ranking.title}</strong>
                                            <span className={styles.meta}>
                                                Top {ranking.size}{ranking.composition ? ` · ${COMPOSITION_LABEL[ranking.composition]}` : ''}
                                            </span>
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        {moreError && <p className={base.errorText} role="alert">Couldn&apos;t load more rankings.</p>}
                        {nextCursor && (
                            <button type="button" className={`${base.secondary} ${styles.more}`} onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? 'Loading…' : 'Load more'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </main>
    );
}
